import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, ExternalLink, Bed, UtensilsCrossed, Heart, ChevronDown } from "lucide-react";
import { Separator } from "@/components/ui/separator";

export default function PlanVisitPage() {
  const [roomsOpen, setRoomsOpen] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-paper py-10 sm:py-16 lg:py-20">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <Link
          to="/"
          data-testid="plan-visit-back-link"
          className="inline-flex items-center text-sm font-sans text-charcoal/60 hover:text-charcoal mb-8 transition-colors"
        >
          <ArrowLeft size={16} className="mr-1" />
          Back to Home
        </Link>

        {/* Page Title */}
        <div className="mb-12 sm:mb-16">
          <h1
            data-testid="plan-visit-heading"
            className="font-serif text-3xl sm:text-4xl font-semibold text-charcoal mb-4"
          >
            Planning Your Visit
          </h1>
          <p className="text-[0.95rem] sm:text-lg font-sans text-charcoal/70 leading-relaxed">
            We are really pleased to help bring family together for this
            historic occasion.
          </p>
          <p className="text-[0.95rem] sm:text-lg font-sans text-charcoal/70 leading-relaxed mt-4">
            To make the weekend easier to plan and more flexible for families,
            accommodation, meals and drinks will generally be arranged or paid
            for directly by attendees. This gives each family the freedom to
            choose what works best for their travel plans, budget and needs,
            while still coming together for the shared reunion moments.
          </p>
        </div>

        {/* ACCOMMODATION */}
        <div className="mb-14 sm:mb-16">
          <div className="flex items-center gap-3 mb-6">
            <Bed size={22} className="text-forest shrink-0" />
            <h2 className="font-serif text-2xl sm:text-3xl font-medium text-charcoal">
              Accommodation
            </h2>
          </div>

          <p className="text-[0.95rem] sm:text-base font-sans text-charcoal/70 leading-relaxed mb-6">
            To help families plan ahead, a hold has been placed on rooms at
            Croydon Lodge for those attending the McCartney Family Reunion.
            Rooms can be booked directly with Croydon Lodge by mentioning the
            McCartney Reunion.
          </p>

          {/* Contact */}
          <div className="bg-parchment border border-sand rounded-md p-5 sm:p-6 mb-6">
            <p className="text-sm font-sans font-semibold uppercase tracking-wider text-bronze mb-3">
              Book Directly
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Mail size={15} className="text-forest shrink-0" />
                <a
                  href="mailto:admin@croydonlodge.co.nz"
                  className="text-[0.95rem] font-sans text-forest hover:text-forest/80 underline underline-offset-2"
                >
                  admin@croydonlodge.co.nz
                </a>
              </div>
              <div className="flex items-center gap-2">
                <ExternalLink size={15} className="text-forest shrink-0" />
                <a
                  href="https://www.croydonlodge.co.nz/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[0.95rem] font-sans text-forest hover:text-forest/80 underline underline-offset-2"
                >
                  www.croydonlodge.co.nz
                </a>
              </div>
            </div>
          </div>

          {/* Room Types - Collapsible */}
          <button
            onClick={() => setRoomsOpen(!roomsOpen)}
            data-testid="toggle-room-types"
            className="w-full flex items-center justify-between border border-sand rounded-md px-5 sm:px-6 py-4 mb-4 text-left hover:bg-parchment/50 transition-colors"
          >
            <span className="text-sm sm:text-base font-sans font-medium text-charcoal">
              View room types and prices
            </span>
            <ChevronDown
              size={18}
              className={`text-bronze transition-transform duration-200 ${roomsOpen ? "rotate-180" : ""}`}
            />
          </button>

          {roomsOpen && (
            <div className="space-y-3 mb-6 animate-fade-in">
              <div className="border border-sand rounded-md p-4 sm:p-5 flex items-start justify-between">
                <div>
                  <p className="text-sm sm:text-base font-sans font-medium text-charcoal">Family Rooms</p>
                  <p className="text-xs sm:text-sm font-sans text-charcoal/55 mt-1">2 Queen beds + 1 Single &middot; Ground floor</p>
                </div>
                <span className="text-sm sm:text-base font-sans font-medium text-forest whitespace-nowrap ml-4">$195/night</span>
              </div>
              <div className="border border-sand rounded-md p-4 sm:p-5 flex items-start justify-between">
                <div>
                  <p className="text-sm sm:text-base font-sans font-medium text-charcoal">Standard Rooms</p>
                  <p className="text-xs sm:text-sm font-sans text-charcoal/55 mt-1">1 Queen bed + 1 Single &middot; Ground floor</p>
                </div>
                <span className="text-sm sm:text-base font-sans font-medium text-forest whitespace-nowrap ml-4">$175/night</span>
              </div>
              <div className="border border-sand rounded-md p-4 sm:p-5 flex items-start justify-between">
                <div>
                  <p className="text-sm sm:text-base font-sans font-medium text-charcoal">Superior Rooms</p>
                  <p className="text-xs sm:text-sm font-sans text-charcoal/55 mt-1">1 King bed (can split to singles) &middot; Upstairs</p>
                </div>
                <span className="text-sm sm:text-base font-sans font-medium text-forest whitespace-nowrap ml-4">$195/night</span>
              </div>
            </div>
          )}
          <p className="text-sm sm:text-[0.95rem] font-sans text-charcoal/60 leading-relaxed mb-4">
            We recommend contacting Croydon Lodge directly by email or phone,
            as we have been advised that online bookings through the website
            may be temporarily unavailable at times.
          </p>

          <div className="bg-paper border border-sand rounded-md p-5 sm:p-6">
            <p className="text-sm sm:text-[0.95rem] font-sans text-charcoal/60 leading-relaxed">
              There are also other local accommodation options in and around
              Gore. If you are looking at alternatives and would like some
              guidance, please feel free to contact us at{" "}
              <a
                href="mailto:mccartneyreunion@gmail.com"
                className="text-forest hover:text-forest/80 underline underline-offset-2"
              >
                mccartneyreunion@gmail.com
              </a>
            </p>
          </div>
        </div>

        <Separator className="mb-14 sm:mb-16 bg-sand" />

        {/* MEALS */}
        <div className="mb-14 sm:mb-16">
          <div className="flex items-center gap-3 mb-6">
            <UtensilsCrossed size={22} className="text-forest shrink-0" />
            <h2 className="font-serif text-2xl sm:text-3xl font-medium text-charcoal">
              Meal Costs & Food Information
            </h2>
          </div>

          <p className="text-[0.95rem] sm:text-base font-sans text-charcoal/70 leading-relaxed mb-6">
            Please allow for your own meal and drink costs during the weekend.
            Further details, including meal options and payment arrangements,
            will be provided as planning continues.
          </p>

          <div className="bg-parchment border border-sand rounded-md p-5 sm:p-6 mb-6">
            <p className="text-sm font-sans font-semibold uppercase tracking-wider text-bronze mb-3">
              Planned Meals
            </p>
            <ul className="space-y-2">
              <li className="text-sm sm:text-[0.95rem] font-sans text-charcoal/70 flex items-start gap-2">
                <span className="text-bronze mt-1.5 text-xs">&#9679;</span>
                Friday Welcome
              </li>
              <li className="text-sm sm:text-[0.95rem] font-sans text-charcoal/70 flex items-start gap-2">
                <span className="text-bronze mt-1.5 text-xs">&#9679;</span>
                Saturday Dinner
              </li>
              <li className="text-sm sm:text-[0.95rem] font-sans text-charcoal/70 flex items-start gap-2">
                <span className="text-bronze mt-1.5 text-xs">&#9679;</span>
                Optional Saturday midday catch-up at Miss Cocoa Coffee, where
                food and drinks will be available for those who wish
              </li>
            </ul>
          </div>

          <p className="text-sm sm:text-[0.95rem] font-sans text-charcoal/55 leading-relaxed">
            We will also share information about children&#39;s meal options and
            dietary requirements closer to the reunion.
          </p>
        </div>

        <Separator className="mb-14 sm:mb-16 bg-sand" />

        {/* SUPPORTING THE REUNION */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-6">
            <Heart size={22} className="text-forest shrink-0" />
            <h2 className="font-serif text-2xl sm:text-3xl font-medium text-charcoal">
              Supporting the Reunion
            </h2>
          </div>

          <p className="text-[0.95rem] sm:text-base font-sans text-charcoal/70 leading-relaxed mb-6">
            Some reunion items, such as the photographer and 150-year
            anniversary cake, are being arranged by the planning group. We
            will provide an update once details are clearer, including any
            small contribution or opportunity for family members who may wish
            to support through sponsorship.
          </p>
        </div>

        {/* Contact */}
        <div className="text-center mt-12 sm:mt-16">
          <p className="text-sm font-sans text-charcoal/45 mb-2">
            Questions about planning your visit?
          </p>
          <a
            href="mailto:mccartneyreunion@gmail.com"
            className="text-sm sm:text-base font-sans text-forest hover:text-forest/80 underline underline-offset-2"
          >
            mccartneyreunion@gmail.com
          </a>
        </div>
      </div>
    </div>
  );
}
